import { useQueryClient } from '@tanstack/react-query';

import { EnvironmentAccessCard } from '@/components/profile/EnvironmentAccessCard';
import { ProfileForm } from '@/components/profile/ProfileForm';
import { SessionsCard } from '@/components/profile/SessionsCard';
import type { AllowedEnvironments } from '@/lib/mode/ModeProvider';
import {
  getAdminProfileGetAdminMeQueryKey,
  getAdminProfileGetAdminMeSessionsQueryKey,
  useAdminProfileGetAdminMe,
  useAdminProfileGetAdminMeSessions,
  useAdminProfilePostAdminMeChangePassword,
  useAdminProfilePostAdminMeSessionsIdGuidRevoke,
  useAdminProfilePostAdminMeSessionsRevokeOthers,
  useAdminProfilePutAdminMe,
} from '@/lib/api/admin-profile/admin-profile';
import { useAuth } from '@/lib/auth/useAuth';

// Pull the human-readable `detail` field from a fetch error that came back
// as RFC 9457 problem-details JSON. orval's fetcher rejects with the parsed
// body when the response is non-2xx and JSON-shaped, so we walk a few
// candidate fields and fall back to the raw error message.
function readApiErrorDetail(err: unknown): string | undefined {
  if (err === null || err === undefined) return undefined;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const r = err as Record<string, unknown>;
    if (typeof r.detail === 'string') return r.detail;
    if (typeof r.title === 'string') return r.title;
    if (typeof r.message === 'string') return r.message;
    const errors = r.errors;
    if (errors && typeof errors === 'object') {
      const first = Object.values(errors as Record<string, unknown>)[0];
      if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
    }
  }
  return undefined;
}

/**
 * Self-service profile page for admin users. Pulls the authenticated admin's
 * row via /admin/me, lets them edit name + email and change password. Email
 * and password changes revoke sibling sessions server-side (the current
 * device stays signed in).
 *
 * The token cache held in `useAuth` only tracks the access token (no email
 * stored client-side once the access token is read), so after a successful
 * email change we trigger a refetch of /admin/me — the UserCard pulls its
 * label from the auth context which we keep in sync via the refetch + reset
 * dance in <ProfileForm>.
 */
export default function ProfilePage() {
  const queryClient = useQueryClient();
  const { refresh } = useAuth();
  const meQuery = useAdminProfileGetAdminMe();
  const putMutation = useAdminProfilePutAdminMe();
  const pwMutation = useAdminProfilePostAdminMeChangePassword();

  if (meQuery.isLoading) {
    return <PageShell><div className="text-sm text-muted-foreground">Loading profile…</div></PageShell>;
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <PageShell>
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
          Could not load your profile. Refresh the page or sign in again.
        </div>
      </PageShell>
    );
  }

  const me = meQuery.data;
  // `allowedEnvironments` is a Phase-B addition; default to `'Both'` until
  // the API ships the field so we don't lock anyone out of the chrome.
  const allowed: AllowedEnvironments =
    (me as { allowedEnvironments?: AllowedEnvironments }).allowedEnvironments ?? 'Both';

  return (
    <PageShell>
      <SessionsPanel />
      <EnvironmentAccessCard allowed={allowed} contact="a SuperAdmin" />
      <ProfileForm
        initial={{ email: me.email, fullName: me.fullName, role: String(me.role) }}
        // Email change is disabled at the UI level. The backend still
        // supports it (and a SuperAdmin can update other users via
        // /admin/users/{id}), but self-service email rotation is intentionally
        // off until an email-verification flow is wired up — otherwise a
        // hijacked access token could lock the legitimate user out.
        allowEmailEdit={false}
        isSavingPersonal={putMutation.isPending}
        isChangingPassword={pwMutation.isPending}
        onSavePersonal={async ({ fullName, email, kind }) => {
          await putMutation.mutateAsync({
            data: { fullName, email },
          }).catch((err) => {
            throw new Error(readApiErrorDetail(err) ?? 'Could not save profile.');
          });
          // Always refetch — name change updates the sidebar, email change
          // forces a JWT refresh because the email claim is now stale.
          await queryClient.invalidateQueries({ queryKey: getAdminProfileGetAdminMeQueryKey() });
          if (kind === 'email-changed') {
            // Pulls a fresh access token so /admin/* keeps working with the
            // new email claim baked in.
            await refresh?.().catch(() => undefined);
          }
        }}
        onChangePassword={async ({ currentPassword, newPassword }) => {
          await pwMutation.mutateAsync({ data: { currentPassword, newPassword } }).catch((err) => {
            throw new Error(readApiErrorDetail(err) ?? 'Could not change password.');
          });
        }}
      />
    </PageShell>
  );
}

/**
 * Sessions list section. Separate component so the hook calls don't have to
 * sit above the early-return guards in <ProfilePage>.
 */
function SessionsPanel() {
  const queryClient = useQueryClient();
  const sessionsQuery = useAdminProfileGetAdminMeSessions();
  const revokeOne = useAdminProfilePostAdminMeSessionsIdGuidRevoke();
  const revokeOthers = useAdminProfilePostAdminMeSessionsRevokeOthers();

  const sessions = (sessionsQuery.data ?? []).map((s) => ({
    id: s.id,
    ipAddress: s.ipAddress,
    userAgent: s.userAgent,
    createdAt: s.createdAt,
    expiresAt: s.expiresAt,
    revokedAt: s.revokedAt,
    isCurrent: s.isCurrent,
  }));

  return (
    <SessionsCard
      sessions={sessions}
      isLoading={sessionsQuery.isLoading}
      onRevokeSession={async (sessionId) => {
        await revokeOne.mutateAsync({ id: sessionId });
        await queryClient.invalidateQueries({ queryKey: getAdminProfileGetAdminMeSessionsQueryKey() });
      }}
      onRevokeOthers={async () => {
        const res = await revokeOthers.mutateAsync();
        await queryClient.invalidateQueries({ queryKey: getAdminProfileGetAdminMeSessionsQueryKey() });
        // orval models int as `number | string` via OpenAPI's int64 pattern;
        // narrow to number for the SessionsCard contract.
        return { revokedCount: Number(res.revokedCount) };
      }}
    />
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Edit your name, email, or password. Sensitive changes sign other
          sessions out.
        </p>
      </header>
      {children}
    </div>
  );
}
